import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  EventEmitter,
  inject,
  Input,
  OnInit,
  Output,
} from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { FormArray, FormControl, FormGroup, Validators } from '@angular/forms';
import { finalize } from 'rxjs';
import { TranslateService } from '@ngx-translate/core';
import { BoardCompanyService } from '../../../../services/board-company.service';
import { NotificationService } from '../../../../services/notifications.service';
import { BoardCompany, NewCardCompany } from '../../board-v3.types';
import {
  customFieldEmailValidator,
  customFieldPhoneValidator,
  normalizePhonePayloadValue,
} from '../../custom-field.validators';
import { getCompanyErrorKey } from '../company.utils';
import { AddressInput } from '../../../../interfaces/card';

export interface CompanyFormResult {
  company: BoardCompany;
}

@Component({
  selector: 'app-board-v3-company-form',
  templateUrl: './company-form.component.html',
  changeDetection: ChangeDetectionStrategy.Eager,
  standalone: false,
  host: { class: 'block h-full min-h-0' },
})
export class BoardV3CompanyFormComponent implements OnInit {
  /** Cards the new company is linked to right away */
  @Input() cardIds: string[] = [];
  @Output() close = new EventEmitter<CompanyFormResult | void>();

  private readonly companyService = inject(BoardCompanyService);
  private readonly notificationService = inject(NotificationService);
  private readonly translateService = inject(TranslateService);
  private readonly destroyRef = inject(DestroyRef);

  protected readonly form = new FormGroup({
    name: new FormControl('', { nonNullable: true, validators: [Validators.required, Validators.maxLength(255)] }),
    phones: new FormArray<FormControl<string>>([]),
    emails: new FormArray<FormControl<string>>([]),
    addresses: new FormControl<AddressInput[]>([], { nonNullable: true }),
  });

  protected saving = false;

  get phones(): FormArray<FormControl<string>> {
    return this.form.controls.phones;
  }

  get emails(): FormArray<FormControl<string>> {
    return this.form.controls.emails;
  }

  ngOnInit(): void {
    this.addPhone();
    this.addEmail();
  }

  protected addPhone(value = ''): void {
    this.phones.push(new FormControl(value, { nonNullable: true, validators: [customFieldPhoneValidator] }));
  }

  protected addEmail(value = ''): void {
    this.emails.push(new FormControl(value, { nonNullable: true, validators: [customFieldEmailValidator] }));
  }

  protected removePhone(index: number): void {
    this.phones.removeAt(index);
    if (!this.phones.length) this.addPhone();
  }

  protected removeEmail(index: number): void {
    this.emails.removeAt(index);
    if (!this.emails.length) this.addEmail();
  }

  protected cancel(): void {
    this.close.emit();
  }

  protected submit(): void {
    this.form.markAllAsTouched();
    if (this.form.invalid || this.saving) return;

    this.saving = true;
    this.companyService
      .createCompany({
        ...this.buildPayload(),
        ...(this.cardIds.length ? { card_ids: this.cardIds } : {}),
      })
      .pipe(
        takeUntilDestroyed(this.destroyRef),
        finalize(() => (this.saving = false)),
      )
      .subscribe({
        next: (company) => {
          this.notificationService.showSuccess('', this.translate('board_v3_company_saved'));
          this.close.emit({ company });
        },
        error: (error) => this.notificationService.showError('', this.translate(getCompanyErrorKey(error))),
      });
  }

  private buildPayload(): NewCardCompany {
    const raw = this.form.getRawValue();

    return {
      name: raw.name.trim(),
      phones: [...new Set(raw.phones.map((phone) => normalizePhonePayloadValue(phone)).filter(Boolean))],
      emails: [...new Set(raw.emails.map((email) => email.trim().toLowerCase()).filter(Boolean))],
      addresses: raw.addresses,
    };
  }

  private translate(key: string): string {
    return String(this.translateService.instant(key));
  }
}
